import {
  useQuery,
  useMutation,
  // useQueryClient,
  type QueryKey,
  useQueryClient
} from "@tanstack/react-query";
import { useAttributesQuery } from "./general";
import { ERRORS } from "../consts";
import { fetcherFactory } from "../services";
import { useAuthStore } from "../stores";
import type { RunsticResponse, UserGoals, UserGoalsConfigs } from "../types";
import { getEnvironmentVariables } from "../utils/general";

const { basePath } = getEnvironmentVariables();

export function useLearnerProgressQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const isLoading = useAuthStore(state => state.isLoading);

  const fetcher = fetcherFactory();
  const uri = `/runtastic/learnerProgress/${corporateId}/${userId}?initiativeId=${initiativeId}`;

  const queryOptions = {
    enabled:
      Boolean(enabled && userId && accessToken && sessionToken && corporateId && !isLoading && initiativeId) &&
      !isSeedingSession,
    queryFn: async () => {
      try {
        const res = await fetcher(`${basePath}${uri}`, {
          headers: {
            Authorization: accessToken,
            "x-ada-session-token": sessionToken
          }
        });

        return await res.json();
      } catch (error) {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
        }
        throw error;
      }
    },
    queryKey: ["userProgress", userId, corporateId, sessionToken],
    refetchOnMount: false, // Cambiato da true per evitare chiamate eccessive
    retry: (failureCount: number, error: unknown) => {
      // Logica di retry personalizzata per evitare cicli infiniti
      if (failureCount >= 2) {
        return false; // Ferma i retry dopo 2 tentativi
      }

      // Non fare retry per errori di autorizzazione
      if (error === ERRORS.UNAUTHORIZED) {
        return false;
      }

      // Controllo per errori con status HTTP
      if (typeof error === "object" && error !== null && "status" in error) {
        const status = (error as { status: number }).status;
        // Non fare retry per la maggior parte degli errori client
        if (status >= 400 && status < 500) {
          return false;
        }
      }

      // Controllo per altri tipi di errore che non dovrebbero essere riprovati
      if (typeof error === "string" && error.includes("Failed to fetch")) {
        // Errori di rete - limita i retry
        if (failureCount >= 1) {
          return false;
        }
      }

      return true;
    },
    retryDelay: (attemptIndex: number) => Math.min(2000 * Math.pow(2, attemptIndex), 10000), // Delay più conservativo
    staleTime: 3 * 60 * 1000
  } as const;

  return useQuery<RunsticResponse>(queryOptions);
}

export function useGetLearnerGoalsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);

  const fetcher = fetcherFactory();
  const uri = `/runtastic/learnerGoals/${corporateId}/${userId}`;

  return useQuery<
      unknown,
      unknown,
      UserGoals,
      QueryKey
    >({
      enabled: Boolean(
        enabled
        && userId
        && accessToken
        && sessionToken
        && corporateId
      ) && !isSeedingSession,
      queryFn: () => fetcher(
        `${basePath}${uri}`,
        {
          headers: {
            "Authorization": accessToken,
            "x-ada-session-token": sessionToken
          },
          method: "GET"
        })
        .then((res) => {
          return res.json();
        })
        .then((data: {data: UserGoals}) => {
          return data;

        })
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error; // rethrow so that react query doesn't complain about undefined return value
          } else {
            throw error;
          }
        }),
      queryKey: [accessToken, sessionToken, uri, isSeedingSession, "getUserGoals"],
      refetchOnMount: true,
      staleTime: 3 * 60 * 1000
    });
}

export function useGetLearnerGoalsConfigsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const isLoading = useAuthStore(state => state.isLoading);

  const fetcher = fetcherFactory();
  const uri = `/runtastic/configs/${corporateId}`;

  return useQuery<
      unknown,
      unknown,
      UserGoalsConfigs,
      QueryKey
    >({
      enabled: Boolean(
        enabled
        && userId
        && accessToken
        && sessionToken
        && corporateId
        && !isLoading
      ) && !isSeedingSession,
      queryFn: () => fetcher(
        `${basePath}${uri}`,
        {
          headers: {
            "Authorization": accessToken,
            "x-ada-session-token": sessionToken
          },
          method: "GET"
        })
        .then((res) => {
          return res.json();
        })
        .then((data: UserGoalsConfigs) => {
          return data;
        })
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error; // rethrow so that react query doesn't complain about undefined return value
          } else {
            throw error;
          }
        }),
      queryKey: [accessToken, sessionToken, uri, isSeedingSession, "getUserGoalsConfigs"],
      refetchOnMount: true,
      staleTime: 3 * 60 * 1000
    });
}

export function usePutLearnerGoalsQuery(plan: string) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const userId = useAuthStore(state => state.userData?.id_user) ?? "";

  const fetcher = fetcherFactory();
  const uri = `/runtastic/learnerGoals/${corporateId}/${userId}`;

  const queryClient = useQueryClient();

  return useMutation({

    mutationFn: () => fetcher(
      `${basePath}${uri}`,
      {
        body: JSON.stringify({
          plan: plan.toUpperCase()
        }),
        headers:{
          "authorization": accessToken,
          "content-type":"application/json",
          "x-ada-session-token": sessionToken
        },
        method: "PUT"
      })
      .catch(async (error) => {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw await error;
        }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["learnerGoals", userId, corporateId, sessionToken] });
    }
  });
}

export function useUserAttributes({ enabled = true }: { enabled?: boolean } = {}) {
  // Ottengo la lista completa degli attributi da useAttributesQuery (ha l'ordine corretto)
  const { data: attributeNames, isError: attributesError } = useAttributesQuery();

  // Ottengo i dati di progresso da useLearnerProgressQuery con enabled condizionale
  const {
    data: progressData,
    isError: progressError,
    isLoading: progressLoading
  } = useLearnerProgressQuery({
    enabled: Boolean(enabled && attributeNames && attributeNames.length > 0)
  });

  // Se ci sono errori negli attributi o nel progresso, ritorno array vuoto
  if (attributesError || progressError) {
    return [];
  }

  // Se non ho la lista degli attributi, ritorno array vuoto
  if (!attributeNames || attributeNames.length === 0) {
    return [];
  }

  // Se i dati di progresso sono in loading, ritorno array vuoto per ora
  if (progressLoading) {
    return [];
  }

  // Uso la lista completa degli attributi come base (ordine corretto)
  // e arricchisco con i dati di progresso quando disponibili
  return attributeNames.map(attr => {
    // Trovo i dati di progresso corrispondenti per questo attributo
    const progressAttr = (progressData as unknown as RunsticResponse)?.attributes?.find((
      progressItem: { attributeId: number | string; completedLos: number; los: number }
    ) =>
      progressItem.attributeId.toString() === attr.attributeId.toString()
    );

    return {
      attributeId: attr.attributeId,
      attributeName: attr.attribute,
      completedLos: progressAttr?.completedLos || 0, // Default a 0 se non ci sono dati di progresso
      los: progressAttr?.los || 0 // Default a 0 se non ci sono dati di progresso
    };
  });
}

export function useUserProgressWithAttributes({ enabled = true }: { enabled?: boolean } = {}): {
  data: RunsticResponse | null;
  isError: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isLoadingError: boolean;
  [key: string]: unknown;
} {
  // Utilizzo direttamente useLearnerProgressQuery per ottenere i dati base
  const progressQuery = useLearnerProgressQuery({ enabled });

  // Ottengo gli attributi processati separatamente
  const { data: attributeNames } = useAttributesQuery();

  // Se la query è in errore, propago lo stato di errore
  if (progressQuery.isError) {
    return {
      ...progressQuery,
      data: null
    };
  }

  // Se la query è in stato di loading o non ha dati, ritorno la query senza modifiche
  if (progressQuery.isLoading || !progressQuery.data) {
    return {
      ...progressQuery,
      data: null
    };
  }

  // Processo gli attributi con i nomi corretti
  const enhancedAttributes = (progressQuery.data as RunsticResponse).attributes?.map(progressAttr => {
    // Trovo il nome dell'attributo corrispondente
    const attributeNameData = attributeNames?.find(nameAttr =>
      nameAttr.attributeId.toString() === progressAttr.attributeId.toString()
    );

    return {
      attributeId: progressAttr.attributeId,
      attributeName: attributeNameData?.attribute || progressAttr.attributeName,
      completedLos: progressAttr.completedLos || 0,
      los: progressAttr.los || 0
    };
  }) || [];

  const enhancedData: RunsticResponse = {
    ...(progressQuery.data as RunsticResponse),
    attributes: enhancedAttributes
  };

  return {
    ...progressQuery,
    data: enhancedData
  };
}

